// src/rms/data/memberService.js
// ============================================================================
// The staff roster
// ============================================================================
// Structured like messageService: one exported function per operation, each
// branching on the backend, each returning the { success, data, error }
// contract. Screens never call supabase.rpc or db.create("members") directly,
// because the two backends do genuinely different things here and the
// difference has to live in exactly one file.
//
// What the two backends have in common is the SHAPE of every answer. What they
// do not share is any pretence: on the local backend there is no email, no
// second user and no RLS, so inviting somebody hands back a link to paste
// rather than claiming to have sent one — the same choice messageService makes
// when it refuses to fake a send.
// ============================================================================

import db, { isLocalBackend } from "./index";
import { supabase } from "../config/supabaseClient";
import { createSuccessResponse, createErrorResponse } from "../utils/response";
import { ROLES } from "./permissions";
import { LOCAL_USER_ID } from "./localAdapter";
import { counterEmail } from "./counterLogin";

/** Where a local invite link points. Mirrors RMS_APP_URL in the edge function. */
const localJoinUrl = (token) =>
  `${window.location.origin}/join?token=${encodeURIComponent(token)}`;

/** Local stand-in for the token table, which does not exist on this backend. */
const LOCAL_TOKENS = "rms.local.inviteTokens";

function readLocalTokens() {
  try {
    return JSON.parse(window.localStorage.getItem(LOCAL_TOKENS) || "{}");
  } catch {
    return {};
  }
}

function writeLocalToken(token, memberId) {
  try {
    const all = readLocalTokens();
    // Tokens are kept OUT of the members collection on purpose: token_hash is
    // not a column on rms_operator_members, and verify-columns reads every key
    // written through db.create("members", ...) against the migrations. A local
    // convenience must not invent a column the real schema does not have.
    window.localStorage.setItem(LOCAL_TOKENS, JSON.stringify({ ...all, [token]: memberId }));
  } catch {
    /* storage unavailable: the link simply will not resolve later */
  }
}

export async function listMembers(operator) {
  if (!operator?.id) return createSuccessResponse([]);
  return db.list("members", { operator_id: operator.id }, { orderBy: "invited_at" });
}

/** The signed-in person's own row, including their role. */
export async function getCurrentMember(operator) {
  if (!operator?.id) return createSuccessResponse(null);
  return db.getCurrentMember(operator.id);
}

/**
 * Invites somebody, at a role the owner chooses.
 *
 * On Supabase this is the edge function, never a direct insert: the token is
 * minted by a service_role-only RPC and must never touch a browser. The role
 * is validated here as well as there — not because the client's check protects
 * anything, but because a typo should produce a sentence rather than a 400.
 */
export async function inviteMember(operator, { email, displayName, role }) {
  if (!operator?.id) return createErrorResponse("No business loaded.");

  const address = String(email || "").trim().toLowerCase();
  if (!address) return createErrorResponse("Enter an email address to invite.");
  if (!ROLES.includes(role)) return createErrorResponse("Choose a role for this person.");

  if (isLocalBackend) {
    // Postgres has UNIQUE (operator_id, email); the local store has no
    // constraints at all. Checking here rather than inside the generic insert
    // means both backends refuse a duplicate with the same sentence, instead
    // of local silently building a roster that would fail on first contact
    // with the real schema.
    const existing = await db.list("members", { operator_id: operator.id });
    if ((existing.data || []).some((m) => (m.email || "").toLowerCase() === address)) {
      return createErrorResponse("That person is already on your team.");
    }

    const created = await db.create("members", {
      operator_id: operator.id,
      email: address,
      display_name: displayName?.trim() || null,
      role,
      status: "invited",
      invited_at: new Date().toISOString(),
      invite_sent_at: new Date().toISOString(),
      invite_expires_at: new Date(Date.now() + 7 * 864e5).toISOString(),
      invited_by: LOCAL_USER_ID,
    });
    if (!created.success) return created;

    const token = `local-${created.data.id}`;
    writeLocalToken(token, created.data.id);

    return createSuccessResponse({
      member: created.data,
      delivered: false,
      // No email exists on this backend, so rather than pretend, hand back the
      // link. Same honesty as messageService's local_only branch.
      joinUrl: localJoinUrl(token),
    });
  }

  const { data, error } = await supabase.functions.invoke("send-invite", {
    body: {
      operator_id: operator.id,
      email: address,
      role,
      display_name: displayName?.trim() || null,
    },
  });

  if (error) {
    // The edge function's messages are written to be read by an operator, and
    // supabase-js buries them in the response body rather than in error.message.
    const detail = await readFunctionError(error);
    return createErrorResponse(detail || "The invitation could not be sent.");
  }
  return createSuccessResponse({ member: data, delivered: !!data?.delivered });
}

/**
 * The owner makes the account outright, with a password they hand over.
 *
 * The normal way somebody joins now. inviteMember stays for the case it is
 * genuinely better at -- a remote bookkeeper the owner would rather not choose
 * a password for -- but nothing depends on an email arriving any more.
 *
 * The password goes to the edge function and no further. It is not returned, it
 * is not stored here, and it must never be logged; the caller is expected to
 * show it once on screen and forget it too.
 */
export async function createStaffAccount(operator, { email, displayName, role, password, notify, reset }) {
  if (!operator?.id) return createErrorResponse("No business loaded.");

  const address = String(email || "").trim().toLowerCase();
  if (!address.includes("@")) return createErrorResponse("That is not an email address.");
  if (!ROLES.includes(role)) return createErrorResponse("Choose a role for this person.");
  // Same minimum as the sign-up screen. A looser rule here would be a quiet
  // back door into weaker passwords than the product otherwise allows.
  if ((password || "").length < 8) return createErrorResponse("Use at least 8 characters.");

  if (isLocalBackend) {
    // No auth server, so there is no account to make. The member row is real
    // and the roster behaves correctly; the password is discarded rather than
    // stored, because storing it would be theatre and the next person to read
    // this file should not have to work out whether it was.
    const existing = await db.list("members", { operator_id: operator.id });
    if ((existing.data || []).some((m) => (m.email || "").toLowerCase() === address)) {
      return createErrorResponse("That person is already on your team.");
    }

    const created = await db.create("members", {
      operator_id: operator.id,
      user_id: LOCAL_USER_ID,
      email: address,
      display_name: displayName?.trim() || null,
      role,
      status: "active",
      invited_at: new Date().toISOString(),
      accepted_at: new Date().toISOString(),
      invited_by: LOCAL_USER_ID,
    });
    if (!created.success) return created;

    return createSuccessResponse({
      member: created.data,
      emailed: false,
      warning: "There is no email on the local backend, so nothing was sent.",
    });
  }

  const { data, error } = await supabase.functions.invoke("create-staff", {
    body: {
      operator_id: operator.id,
      email: address,
      password,
      role,
      display_name: displayName?.trim() || null,
      ...(notify === false ? { notify: false } : {}),
      ...(reset ? { reset: true } : {}),
    },
  });

  if (error) {
    const detail = await readFunctionError(error);
    return createErrorResponse(detail || "The account could not be created.");
  }
  return createSuccessResponse(data);
}

/**
 * A member naming themselves.
 *
 * Needs an RPC rather than an update: rms_members_write is owner-only, which is
 * right for the roster but means a staff member cannot write the one field on
 * that row that is genuinely theirs. rms_set_my_display_name touches one column
 * on the caller's own row and nothing else.
 */
export async function setMyDisplayName(operator, name) {
  const clean = String(name || "").trim().replace(/\s+/g, " ");
  if (!clean) return createErrorResponse("Enter your name.");
  if (clean.length > 80) return createErrorResponse("That name is too long.");

  if (isLocalBackend) {
    const me = await getCurrentMember(operator);
    if (!me.data?.id) return createErrorResponse("You are not a member of this business.");
    const updated = await db.update("members", me.data.id, { display_name: clean });
    return updated.success ? createSuccessResponse(clean) : updated;
  }

  const { data, error } = await supabase.rpc("rms_set_my_display_name", {
    p_operator_id: operator.id,
    p_name: clean,
  });
  return error ? createErrorResponse(error.message) : createSuccessResponse(data);
}

/**
 * The shared counter login: a name and a password the whole shift knows.
 *
 * Reuses createStaffAccount entirely. That is the point -- it is an ordinary
 * staff member with a synthetic address, not a special case anywhere in the
 * security model. RLS, the roles and every PIN-signed write treat it exactly as
 * they treat a named staff account.
 */
export async function setCounterLogin(operator, { loginName, password }) {
  const email = counterEmail(loginName);
  if (!email) {
    return createErrorResponse("Use at least three letters or numbers for the counter name.");
  }
  return createStaffAccount(operator, {
    email,
    displayName: loginName.trim(),
    role: "staff",
    password,
    // No inbox behind it, and `reset` so an owner can rotate a password that
    // everybody who ever worked here knows.
    notify: false,
    reset: true,
  });
}

/** supabase-js wraps a non-2xx function reply; the useful sentence is inside. */
async function readFunctionError(error) {
  try {
    const body = await error.context?.json?.();
    return body?.error || null;
  } catch {
    return error?.message || null;
  }
}

export async function resendInvite(operator, member) {
  return inviteMember(operator, {
    email: member.email,
    displayName: member.display_name,
    role: member.role,
  });
}

export async function revokeInvite(member) {
  if (isLocalBackend) {
    return db.remove("members", member.id);
  }
  const { error } = await supabase.rpc("rms_revoke_staff_invite", { p_member_id: member.id });
  return error ? createErrorResponse(error.message) : createSuccessResponse(true);
}

/**
 * Redeems a token.
 *
 * The one operation an invitee performs before they are a member of anything,
 * which is why the Supabase side is a SECURITY DEFINER RPC: there is no policy
 * they could satisfy, so nothing under their own privileges would work.
 */
export async function acceptInvite(token) {
  if (!token) return createErrorResponse("This invitation is not valid or has expired.");

  if (isLocalBackend) {
    const memberId = readLocalTokens()[token];
    if (!memberId) return createErrorResponse("This invitation is not valid or has expired.");

    const updated = await db.update("members", memberId, {
      user_id: LOCAL_USER_ID,
      status: "active",
      accepted_at: new Date().toISOString(),
      invite_expires_at: null,
    });
    if (!updated.success) return updated;
    return createSuccessResponse({
      operator_id: updated.data.operator_id,
      role: updated.data.role,
    });
  }

  const { data, error } = await supabase.rpc("rms_accept_staff_invite", { p_token: token });
  if (error) return createErrorResponse(error.message);
  return createSuccessResponse(data);
}

export async function changeRole(member, role) {
  if (!ROLES.includes(role)) return createErrorResponse("Unknown role.");
  return db.update("members", member.id, { role });
}

/**
 * Suspends or restores somebody.
 *
 * Suspending rather than removing is the reversible half: rms_is_operator_member
 * requires status = 'active', so a suspended member loses access immediately
 * while their history stays attached to them.
 */
export async function setStatus(member, status) {
  if (!["active", "suspended"].includes(status)) {
    return createErrorResponse("Unknown status.");
  }
  if (status === "active" && !member.user_id) {
    return createErrorResponse("They have not accepted their invitation yet.");
  }
  return db.update("members", member.id, { status });
}

export async function removeMember(member) {
  return db.remove("members", member.id);
}

/** Local backend only — see localAdapter.getCurrentMember. */
export async function setViewAsRole(role) {
  return db.setViewAsRole(role);
}

export default {
  listMembers,
  getCurrentMember,
  inviteMember,
  createStaffAccount,
  setCounterLogin,
  setMyDisplayName,
  resendInvite,
  revokeInvite,
  acceptInvite,
  changeRole,
  setStatus,
  removeMember,
  setViewAsRole,
};
